import React, { useRef, useEffect, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import AuthService from "../../utils/AuthService";
import ApiMap from "../../utils/ApiMap";
import axios from "axios";
import {
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormGroup,
  Checkbox,
} from "@mui/material";

const MCMAQuestion = ({
  currentActivity,
  answers,
  handleOptionChange,
  submitted,
  reviewedContent,
  setReviewedContent,
}) => {
  let selectedAnswers = [];
  let correctAnswers = [];
  let isCorrect = false;
  let isIncorrect = false;
  let isPartiallyCorrect = false;

  const [lessonContent, setLessonContent] = useState(null);
  const [isLessonLoading, setIsLessonLoading] = useState(false);
  const [lessonError, setLessonError] = useState(null);
  const showContent = reviewedContent?.[currentActivity?.ContentId] || false;

  const rationaleRef = useRef(null);

  const toggleShowContent = () => {
    setReviewedContent((prev) => ({
      ...prev,
      [currentActivity?.ContentId]: !prev[currentActivity?.ContentId],
    }));
  };

  useEffect(() => {
    if (submitted && rationaleRef.current) {
      setTimeout(() => {
        rationaleRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [submitted]);

  useEffect(() => {
    if (!submitted) return;
    const rationaleWithContent = currentActivity?.Options?.find(
      (opt) => opt.Rationale?.contentId
    );

    if (!rationaleWithContent?.Rationale?.contentId) return;

    const { lessonId, moduleId, courseId, chapterId } =
      rationaleWithContent.Rationale.contentId;

    const fetchLessonContent = async () => {
      setIsLessonLoading(true);
      try {
        const resp = await axios(
          `${ApiMap.CONTENT}/coursecontent/${courseId}/module/${moduleId}/chapter/${chapterId}/lesson/${lessonId}`,
          AuthService.getAuthHeader()
        );
        setLessonContent(resp.data);
      } catch (err) {
        setLessonError("Failed to load lesson content.");
      } finally {
        setIsLessonLoading(false);
      }
    };

    if (AuthService.isLoggedIn()) {
      fetchLessonContent();
    }
  }, [submitted, currentActivity]);

  const correctAnswersCount = currentActivity?.Options.filter(
    (option) => option.IsCorrect === true
  ).length;

  if (submitted) {
    selectedAnswers = Array.isArray(answers[currentActivity?.ContentId])
      ? answers[currentActivity?.ContentId]
      : [answers[currentActivity?.ContentId]].filter((v) => v !== undefined);

    correctAnswers = currentActivity.Options.filter((opt) => opt.IsCorrect);

    const selectedAnswerIds = selectedAnswers.map(Number);
    const correctAnswerIds = correctAnswers.map((opt) => opt.id);

    isCorrect =
      selectedAnswerIds.length > 0 &&
      selectedAnswerIds.every((id) => correctAnswerIds.includes(id)) &&
      correctAnswerIds.every((id) => selectedAnswerIds.includes(id));

    isIncorrect = selectedAnswerIds.some(
      (id) => !correctAnswerIds.includes(id)
    );

    isPartiallyCorrect =
      !isCorrect &&
      selectedAnswerIds.some((id) => correctAnswerIds.includes(id));
  }

  const isValidImageUrl = (value) =>
    typeof value === "string" &&
    (value.endsWith(".png") ||
      value.endsWith(".jpg") ||
      value.endsWith(".jpeg") ||
      value.startsWith("http"));

  return (
    <>
      <FormControl component="fieldset" fullWidth>
        {correctAnswersCount > 1 ? (
          <FormGroup
            sx={{
              display: "grid",
              gridTemplateColumns:
                currentActivity?.Options.length > 5 ? "repeat(2, 1fr)" : "auto",
              gap: 1,
            }}
          >
            {currentActivity?.Options.map((option) => (
              <FormControlLabel
                key={option.id}
                control={
                  <Checkbox
                    checked={
                      answers[currentActivity?.ContentId]?.includes(
                        option.id
                      ) || false
                    }
                    onChange={(e) =>
                      handleOptionChange(
                        currentActivity?.ContentId,
                        option.id,
                        e.target.checked
                      )
                    }
                    sx={{
                      "& .MuiSvgIcon-root": { fontSize: 18, marginRight: 1 },
                    }}
                    disabled={submitted}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isValidImageUrl(option.Value) ? (
                      <img
                        src={option.Value}
                        alt={`Option ${option.id}`}
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "contain",
                        }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ) : (
                      <Typography>{option.Value}</Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </FormGroup>
        ) : (
          <RadioGroup
            value={answers[currentActivity?.ContentId] || ""}
            onChange={(e) =>
              handleOptionChange(
                currentActivity?.ContentId,
                Number(e.target.value)
              )
            }
          >
            {currentActivity?.Options.map((option) => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={
                  <Radio sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isValidImageUrl(option.Value) ? (
                      <img
                        src={option.Value}
                        alt={`Option ${option.id}`}
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "contain",
                        }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    ) : (
                      <Typography>{option.Value}</Typography>
                    )}
                  </Box>
                }
                disabled={submitted}
              />
            ))}
          </RadioGroup>
        )}
      </FormControl>

      {submitted && (
        <Box
          ref={rationaleRef}
          sx={{
            marginTop: 2,
            padding: 2,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            backgroundColor: isCorrect
              ? "#e8f5e9"
              : isIncorrect
              ? "#ffebee"
              : isPartiallyCorrect
              ? "#fff3e0"
              : "transparent",
          }}
        >
          {isCorrect && (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "1rem",
                  color: "green",
                  marginTop: 1,
                  textAlign: "center",
                }}
              >
                Correct!
              </Typography>
            </>
          )}

          {isIncorrect && (
            <div ref={rationaleRef}>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CancelIcon sx={{ fontSize: 40, color: "red" }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "1rem",
                  color: "red",
                  marginBottom: 1,
                  textAlign: "center",
                }}
              >
                Incorrect!
              </Typography>

              {currentActivity?.Options?.some((opt) => opt.Rationale) ? (
                currentActivity.Options.map((opt, index) => {
                  const rationale = opt.Rationale;

                  if (!rationale) return null;

                  if (typeof rationale === "string" && rationale.trim()) {
                    return (
                      <Typography
                        key={index}
                        sx={{ fontSize: "0.9rem", color: "#444", marginTop: 1 }}
                      >
                        {rationale}
                      </Typography>
                    );
                  }

                  if (rationale.contentId) {
                    return (
                      <Box key={index} sx={{ mt: 2 }}>
                        <Typography
                          onClick={toggleShowContent}
                          sx={{
                            color: "#1976d2",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: "0.95rem",
                            mb: 1,
                          }}
                        >
                          {showContent
                            ? "Hide lesson content"
                            : "Review lesson content to Retake?"}
                        </Typography>

                        {showContent && (
                          <Box
                            sx={{
                              p: 2,
                              border: "1px solid #ccc",
                              borderRadius: 2,
                              mt: 1,
                            }}
                          >
                            {isLessonLoading ? (
                              <Typography>Loading lesson content...</Typography>
                            ) : lessonError ? (
                              <Typography color="error">
                                {lessonError}
                              </Typography>
                            ) : lessonContent ? (
                              <>
                                <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                                  Please refer to the lesson below:
                                </Typography>
                                <Box
                                  sx={{
                                    fontSize: "0.9rem",
                                    color: "#333",
                                    width: "100%",
                                    overflowX: "auto",
                                    "& img": {
                                      maxWidth: "100%",
                                      height: "auto",
                                      display: "block",
                                      margin: "0 auto",
                                    },
                                    "& table": {
                                      width: "100%",
                                      borderCollapse: "collapse",
                                    },
                                    "& th, & td": {
                                      border: "1px solid #ccc",
                                      padding: "8px",
                                      textAlign: "left",
                                      wordBreak: "break-word",
                                    },
                                    "& *": {
                                      boxSizing: "border-box",
                                    },
                                    "& p": {
                                      marginBottom: "0.5em",
                                    },
                                    "& div": {
                                      maxWidth: "100%",
                                      overflowWrap: "break-word",
                                    },
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: lessonContent?.Value,
                                  }}
                                />
                              </>
                            ) : null}
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  return null;
                })
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontStyle: "italic",
                    color: "#999",
                    marginTop: 1,
                  }}
                >
                  Rationale: Not available
                </Typography>
              )}
            </div>
          )}
        </Box>
      )}
    </>
  );
};

export default MCMAQuestion;
